<template>
  <section class="member-identity-page">
    <div class="identity-heading">
      <div><h2>身份认证</h2><p>完善本人资料，查看认证审核进度</p></div>
      <button class="identity-back" type="button" @click="navigate('/member')"><SiteIcon name="arrow-right" :size="15" />返回个人资料</button>
    </div>
    <PageState v-if="!isLoggedIn" title="请先登录" description="登录后可提交认证资料并查看审核进度"><button class="primary" @click="openAuth('login')">登录</button></PageState>
    <PageState v-else-if="loading || loadError" :loading="loading" :error="loadError" @retry="load" />
    <template v-else>
      <div class="identity-status" :class="{ 'is-approved': approved, 'is-warning': ['REJECTED', 'SUSPENDED'].includes(kyc.status) }" role="status">
        <span class="identity-status-icon"><SiteIcon :name="approved ? 'seal-check' : 'identification-card'" :size="23" /></span>
        <div class="identity-status-copy">
          <b>{{ statusLabel }}</b>
          <p v-if="kyc.documentNoMasked">证件号码：{{ kyc.documentNoMasked }}</p>
          <p v-if="kyc.rejectReason">审核意见：{{ kyc.rejectReason }}</p>
          <button v-if="kyc.status === 'PENDING'" class="table-action" type="button" @click="load">刷新审核状态</button>
        </div>
      </div>
      <form v-if="!approved" class="identity-form" @submit.prevent="submit">
        <fieldset :disabled="submitting || kyc.status === 'PENDING'">
          <div class="identity-form-section">
            <h3>基础信息</h3>
            <div class="identity-details-grid">
              <label class="identity-text-field"><span>真实姓名</span><input v-model.trim="form.realName" required maxlength="30" autocomplete="name" placeholder="请输入真实姓名" /></label>
              <label class="identity-text-field"><span>证件类型</span><select v-model="form.documentType"><option value="ID_CARD">居民身份证</option><option value="PASSPORT">护照</option></select></label>
              <label class="identity-text-field identity-document-number"><span>证件号码</span><input v-model.trim="form.documentNo" required minlength="5" maxlength="30" autocomplete="off" placeholder="请输入证件号码" /></label>
            </div>
            <p class="identity-help">{{ form.documentType === 'ID_CARD' ? '居民身份证认证需先绑定本人手机号，并核验姓名、身份证和手机号是否一致。' : '请提交本人护照资料，提交后进入后台审核。' }}</p>
          </div>
          <div class="identity-form-section">
            <h3>证件照片<span>请保证图片清晰、信息完整</span></h3>
            <div class="identity-uploads">
              <div v-for="side in ['front', 'back']" :key="side" class="identity-image-field">
                <div class="identity-image-heading"><span>{{ side === 'front' ? '证件正面' : '证件背面' }}</span><small>{{ side === 'back' && form.documentType === 'PASSPORT' ? '选填' : '必填' }}</small></div>
                <label class="identity-upload-card" :class="{ 'has-image': images[side].preview || images[side].url }">
                  <input :aria-label="side === 'front' ? '上传证件正面' : '上传证件背面'" type="file" accept=".png,.jpg,.jpeg,.webp" @change="selectImage($event, side)" />
                  <span class="identity-upload-preview">
                    <img v-if="images[side].preview || images[side].url" :src="images[side].preview || images[side].url" :alt="side === 'front' ? '证件正面预览' : '证件背面预览'" />
                    <template v-else><span class="identity-document-icon"><SiteIcon :name="side === 'front' ? 'identification-card' : 'receipt'" :size="40" /></span><span class="identity-upload-placeholder">{{ side === 'front' ? '上传证件正面照片' : '上传证件背面照片' }}</span></template>
                  </span>
                  <span class="identity-upload-footer">
                    <span class="identity-upload-caption"><strong>{{ images[side].preview || images[side].url ? '更换图片' : '点击上传' }}</strong><span :title="images[side].file?.name">{{ images[side].file?.name || (images[side].url ? '已保存的证件照片' : '从本地选择图片') }}</span></span>
                    <span class="identity-upload-action" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"><path d="M12 15V4m-4 4 4-4 4 4M5 15v4a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1v-4" /></svg></span>
                  </span>
                </label>
              </div>
            </div>
            <p class="identity-upload-help">支持 PNG、JPG、JPEG、WEBP 格式，每张不超过 10MB</p>
          </div>
          <div class="identity-form-actions">
            <label class="identity-consent"><input v-model="form.consent" type="checkbox" /><span>{{ form.documentType === 'ID_CARD' ? '本人同意将姓名、身份证和已绑定手机号提交认证服务商核验，并将证件资料用于身份审核' : '本人同意平台将所提交的护照资料用于身份审核' }}</span></label>
            <ErrorNotice v-if="submitError" class="identity-error" :message="submitError"></ErrorNotice>
            <button class="primary" type="submit" :disabled="!ready || submitting">{{ submitting ? '提交中…' : kyc.status === 'REJECTED' || kyc.status === 'SUSPENDED' ? '重新提交认证' : '提交认证' }}</button>
          </div>
        </fieldset>
      </form>
    </template>
  </section>
</template>

<script setup>
import { reportError } from '../../services/feedback.js'

import ErrorNotice from '../feedback/ErrorNotice.vue'
import { computed, onBeforeUnmount, reactive, ref, watch } from 'vue'
import { useAppContext } from '../../composables/appContext'
import { webApi } from '../../services/webApi'
import PageState from '../feedback/PageState.vue'
import SiteIcon from '../SiteIcon.vue'

const { isLoggedIn, userName, memberProfile, memberSecurity, openAuth, navigate } = useAppContext()
const loading = ref(false), loadError = ref(''), submitting = ref(false), submitError = ref('')
const kyc = ref({ status: 'NOT_SUBMITTED' })
const form = reactive({ realName: '', documentType: 'ID_CARD', documentNo: '', consent: false })
const images = reactive({ front: { file: null, preview: '', url: '' }, back: { file: null, preview: '', url: '' } })
const approved = computed(() => ['APPROVED', 'VERIFIED'].includes(kyc.value.status))
const statusLabel = computed(() => ({ PENDING: '认证审核中', APPROVED: '认证已通过', VERIFIED: '认证已通过', REJECTED: '认证未通过，请补充后重新提交', SUSPENDED: '认证已暂停，请补充后重新提交' }[kyc.value.status] || '尚未提交认证'))
const ready = computed(() => /^[\u4e00-\u9fa5A-Za-z·\s]{2,30}$/.test(form.realName) && form.documentNo.length >= 5 && form.documentNo.length <= 30 && (form.documentType !== 'ID_CARD' || /^\d{17}[\dXx]$/.test(form.documentNo)) && Boolean(images.front.file || images.front.url) && (form.documentType === 'PASSPORT' || Boolean(images.back.file || images.back.url)) && form.consent && kyc.value.status !== 'PENDING' && !approved.value)
let generation = 0

function resetImages() {
  for (const side of ['front', 'back']) {
    if (images[side].preview) URL.revokeObjectURL(images[side].preview)
    images[side] = { file: null, preview: '', url: '' }
  }
}

function setStatus(status) {
  kyc.value = status || { status: 'NOT_SUBMITTED' }
  if (memberProfile.value) memberProfile.value = { ...memberProfile.value, kycStatus: kyc.value.status }
  if (memberSecurity.value) memberSecurity.value = { ...memberSecurity.value, kycStatus: kyc.value.status }
}

async function load() {
  const ticket = ++generation
  resetImages()
  kyc.value = { status: 'NOT_SUBMITTED' }
  Object.assign(form, { realName: '', documentType: 'ID_CARD', documentNo: '', consent: false })
  loadError.value = ''; submitError.value = ''; submitting.value = false
  if (!isLoggedIn.value) { loading.value = false; return }
  loading.value = true
  try {
    const status = await webApi.memberKyc()
    if (ticket !== generation) return
    setStatus(status)
    form.realName = status?.realName || memberProfile.value?.realName || ''
    form.documentType = status?.documentType === 'PASSPORT' ? 'PASSPORT' : 'ID_CARD'
    images.front.url = status?.frontImageUrl || ''
    images.back.url = status?.backImageUrl || ''
  } catch (error) { if (ticket === generation) loadError.value = error.message || '认证资料加载失败' }
  finally { if (ticket === generation) loading.value = false }
}

function selectImage(event, side) {
  if (submitting.value || kyc.value.status === 'PENDING') return
  const file = event.target.files?.[0]
  if (!file) return
  if (!/\.(png|jpe?g|webp)$/i.test(file.name) || !['image/png', 'image/jpeg', 'image/webp'].includes(file.type) || file.size <= 0 || file.size > 10 * 1024 * 1024) {
    submitError.value = reportError('请选择不超过10MB的PNG、JPG、JPEG或WEBP图片')
    event.target.value = ''
    return
  }
  if (images[side].preview) URL.revokeObjectURL(images[side].preview)
  images[side] = { file, preview: URL.createObjectURL(file), url: '' }
  submitError.value = ''
}

async function submit() {
  if (!isLoggedIn.value || !ready.value || submitting.value || loading.value) return
  const ticket = generation
  submitting.value = true; submitError.value = ''
  try {
    for (const side of ['front', 'back']) {
      if (!images[side].file || images[side].url) continue
      const body = new FormData()
      body.append('file', images[side].file)
      const uploaded = await webApi.uploadKycImage(body)
      if (ticket !== generation) return
      if (!uploaded?.fileUrl) throw new Error('证件图片上传失败，请重新选择')
      images[side].url = uploaded.fileUrl
    }
    if (ticket !== generation) return
    const status = await webApi.submitMemberKyc({ realName: form.realName, documentType: form.documentType, documentNo: form.documentNo.toUpperCase(), frontImageUrl: images.front.url, backImageUrl: images.back.url, kycConsent: 1 })
    if (ticket !== generation) return
    setStatus(status)
    form.documentNo = ''; form.consent = false
  } catch (error) { if (ticket === generation) submitError.value = error.message || '提交实名认证失败' }
  finally { if (ticket === generation) submitting.value = false }
}

watch([isLoggedIn, userName], load, { immediate: true })
onBeforeUnmount(() => { generation++; resetImages() })
</script>

<style scoped>
.member-identity-page { padding: 28px 32px 32px; background: #fff; border-radius: 14px; color: #303442; }
.identity-heading { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; margin-bottom: 24px; }
.identity-heading h2 { margin: 0; font-size: 22px; font-weight: 600; line-height: 1.5; }
.identity-heading p { margin: 6px 0 0; color: #8992a6; font-size: 13px; line-height: 1.6; }
.identity-back { display: inline-flex; align-items: center; gap: 6px; flex: none; min-height: 36px; padding: 8px 0 8px 12px; background: transparent; color: #7d88a0; font-size: 13px; }
.identity-back .site-icon { transform: rotate(180deg); }
.identity-back:hover { color: #328bd0; }
.identity-status { display: flex; align-items: flex-start; gap: 12px; max-width: 780px; margin-bottom: 28px; padding: 16px 18px; border: 1px solid #e2edf8; border-radius: 10px; background: #f6faff; color: #48719a; }
.identity-status-icon { display: grid; place-items: center; flex: none; width: 38px; height: 38px; border-radius: 10px; background: #e8f2ff; color: #539bd6; }
.identity-status-copy { min-width: 0; align-self: center; font-size: 13px; line-height: 1.7; overflow-wrap: anywhere; }
.identity-status-copy b { display: block; font-size: 14px; font-weight: 600; }
.identity-status-copy p { margin: 5px 0 0; }
.identity-status-copy .table-action { margin-top: 8px; padding: 0; }
.identity-status.is-warning { border-color: #f2e3cf; background: #fffbf5; color: #93703e; }
.identity-status.is-warning .identity-status-icon { color: #bd9155; background: #fbefd9; }
.identity-status.is-approved { border-color: #d7ede5; background: #f5fbf8; color: #3c846d; }
.identity-status.is-approved .identity-status-icon { color: #3c9879; background: #e2f3eb; }
.identity-form { max-width: 780px; }
.identity-form fieldset { display: grid; gap: 28px; min-width: 0; margin: 0; padding: 0; border: 0; }
.identity-form-section h3 { display: flex; align-items: baseline; flex-wrap: wrap; gap: 8px 12px; margin: 0 0 18px; font-size: 15px; font-weight: 600; line-height: 1.5; }
.identity-form-section h3 > span { color: #929bad; font-size: 12px; font-weight: 400; }
.identity-details-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 18px 20px; }
.identity-text-field { display: flex; flex-direction: column; gap: 9px; min-width: 0; color: #58647b; font-size: 13px; }
.identity-document-number { grid-column: 1 / -1; }
.identity-text-field input, .identity-text-field select { box-sizing: border-box; width: 100%; min-width: 0; height: 44px; padding: 10px 14px; border: 1px solid #e2e7ef; border-radius: 8px; background: #fff; color: #303e54; font: inherit; font-size: 14px; transition: border-color .15s, box-shadow .15s; }
.identity-text-field input::placeholder { color: #a5adbc; }
.identity-text-field :is(input, select):hover { border-color: #b5cde3; }
.identity-text-field :is(input, select):focus-visible { outline: 0; border-color: #64a9df; box-shadow: 0 0 0 3px #eaf4fe; }
.identity-help { margin: 12px 0 0; color: #8993a6; font-size: 12px; line-height: 1.8; }
.identity-uploads { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 20px; }
.identity-image-field { min-width: 0; }
.identity-image-heading { display: flex; align-items: center; gap: 8px; margin-bottom: 10px; color: #58647b; font-size: 13px; }
.identity-image-heading small { padding: 2px 6px; border-radius: 4px; background: #f1f5f9; color: #8d99ab; font-size: 10px; line-height: 1.3; }
.identity-upload-card { position: relative; display: block; overflow: hidden; border: 1px dashed #cddcea; border-radius: 12px; cursor: pointer; transition: border-color .15s, box-shadow .15s; }
.identity-upload-card:hover, .identity-upload-card:focus-within { border-color: #67a9dc; box-shadow: 0 0 0 3px #eef6fe; }
.identity-upload-card.has-image { border-style: solid; border-color: #dce6f0; }
.identity-upload-card > input { position: absolute; z-index: 1; inset: 0; width: 100%; height: 100%; margin: 0; opacity: 0; cursor: inherit; }
.identity-upload-preview { position: relative; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 12px; aspect-ratio: 1.9; min-height: 150px; background: #f7fafd; }
.identity-document-icon { display: grid; place-items: center; width: 72px; height: 56px; border: 1px solid #d8e4ef; border-radius: 9px; background: #fff; color: #adc4d9; box-shadow: 0 3px 8px rgb(83 120 154 / 4%); }
.identity-upload-placeholder { color: #9ba9ba; font-size: 12px; }
.identity-upload-preview img { position: absolute; inset: 0; display: block; width: 100%; height: 100%; padding: 12px; object-fit: contain; }
.identity-upload-footer { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 14px 16px; border-top: 1px solid #edf1f6; background: #fff; }
.identity-upload-caption { display: flex; flex-direction: column; gap: 4px; min-width: 0; }
.identity-upload-caption strong { color: #559ad0; font-size: 13px; font-weight: 500; }
.identity-upload-caption > span { overflow: hidden; color: #98a2b3; font-size: 11px; white-space: nowrap; text-overflow: ellipsis; }
.identity-upload-action { display: grid; place-items: center; flex: none; width: 30px; height: 30px; border-radius: 8px; background: #eef6ff; color: #67a6d7; }
.identity-upload-action svg { width: 17px; height: 17px; }
.identity-upload-help { margin: 12px 0 0; color: #929bad; font-size: 12px; line-height: 1.7; }
.identity-form-actions { display: grid; gap: 18px; padding-top: 22px; border-top: 1px solid #edf0f5; }
.identity-consent { display: flex; align-items: flex-start; gap: 8px; color: #8490a4; font-size: 12px; line-height: 1.8; cursor: pointer; }
.identity-consent input { flex: none; width: 15px; height: 15px; margin: 3px 0 0; accent-color: #539fdb; }
.identity-error { display: flex; align-items: baseline; gap: 8px; margin: 0; padding: 11px 14px; border-radius: 8px; background: #fff4f3; color: #c3635d; font-size: 13px; line-height: 1.6; overflow-wrap: anywhere; }
.identity-error::before { content: '!'; display: inline-grid; place-items: center; flex: none; width: 15px; height: 15px; border: 1px solid currentColor; border-radius: 50%; font-size: 11px; }
.identity-form .primary { justify-self: start; min-width: 180px; min-height: 44px; padding: 12px 28px; border-radius: 8px; background: linear-gradient(100deg, #66b4ee, #4899ed); box-shadow: 0 4px 12px rgb(72 153 237 / 14%); font-size: 14px; font-weight: 500; }
.identity-form .primary:disabled { background: #edf0f5; box-shadow: none; color: #a0a9b8; cursor: not-allowed; transform: none; }
.identity-form fieldset:disabled { opacity: .65; }
.identity-form fieldset:disabled .identity-upload-card { cursor: not-allowed; }
.identity-form fieldset:disabled .identity-upload-card:hover { border-color: #cddcea; box-shadow: none; }
.identity-back:focus-visible, .identity-consent input:focus-visible { outline: 2px solid #64a9df; outline-offset: 4px; border-radius: 4px; }
@media (max-width: 999px) { .member-identity-page { padding: 24px; } .identity-uploads { gap: 14px; } .identity-upload-preview { min-height: 140px; } }
@media (max-width: 600px) { .member-identity-page { padding: 20px 16px; } .identity-heading { gap: 12px; margin-bottom: 20px; } .identity-heading h2 { font-size: 20px; } .identity-heading p { font-size: 12px; } .identity-back { padding-left: 0; font-size: 12px; } .identity-back .site-icon { display: none; } .identity-status { padding: 14px; } .identity-details-grid, .identity-uploads { grid-template-columns: minmax(0, 1fr); } .identity-upload-preview { aspect-ratio: 2; } .identity-form .primary { width: 100%; } }
</style>
